import React, {Component} from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import {pathOr} from 'ramda'

import RoomHeader from './RoomHeader'
import RoomField from './RoomField'
import {
  FILTER_ROOMS,
  ADULTS_DEFAULT,
  ADULTS_ONE_DAY_BOOKING,
  ADULTS_MIN,
  ADULTS_MAX,
  CHILDREN_DEFAULT,
  CHILDREN_MIN,
  CHILDREN_MAX,
  CHILDREN_AGE_DEFAULT,
  CHILDREN_AGE_MIN,
  CHILDREN_AGE_MAX,
} from '../../../constants'
import {pushOneMoreRoom} from '../../../../../helpers/gtm'
import PlusMinusField from '../../../../shared/FormElements/PlusMinusControl';
import RoomSelectField from './RoomSelectField';
import Button from '../../../../shared/Button';

import styles from './Rooms.module.scss'

const cx = classnames.bind(styles)

const defaultAdults = ADULTS_DEFAULT
const isMultipleBookingEnabled = pathOr(true, ['drupalSettings', 'search', 'isMultipleBookingEnabled'], window)
const oneDayBooking = pathOr(null, ['drupalSettings', 'oneDayBooking'], window)

const FIELD_ADULTS = 'adults'
const FIELD_CHILDREN = 'children'
const FIELD_CHILDREN_AGE = 'childrenAge'

const MAX_ROOMS = 3

class Rooms extends Component {
  roomsVisible = 0

  onGuestsChange = (value, field) => {
    const {guestsChangeAction, filterApplyAction} = this.props
    guestsChangeAction(field, value)
    filterApplyAction(FILTER_ROOMS, false)
  }

  onChildrenCountChange = (value, field, data) => {
    const childAges = [...(data.childrenAge || [])];
    const fieldInfo = this.fieldInfo(field)
    const childrenAgeFieldName = this.fieldName(FIELD_CHILDREN_AGE, fieldInfo.key)
    this.onGuestsChange(value, field)
    while (childAges.length < value) {
      childAges.push(CHILDREN_AGE_DEFAULT)
    }
    while (childAges.length > value) {
      childAges.pop()
    }
    this.onGuestsChange(childAges, childrenAgeFieldName)
  }

  onChildrenAgeUpdate = (data, childIndex, value) => {
    const newAges = [...data.childrenAge]
    const childrenAgeFieldName = this.fieldName(FIELD_CHILDREN_AGE, data.id)

    newAges.splice(childIndex, 1, value)
    this.onGuestsChange(newAges, childrenAgeFieldName)
  }

  onAddRoom = (e) => {
    if (e) {
      e.stopPropagation()
    }
    pushOneMoreRoom()
    const {addRoomAction} = this.props
    addRoomAction()
  }

  onRemoveRoom = roomId => (
    (e) => {
      e.stopPropagation()
      const {removeRoomAction, filterApplyAction} = this.props
      removeRoomAction(roomId)
      filterApplyAction(FILTER_ROOMS, true)
    }
  )

  fieldName = (name, key) => `${name}_${key}`
  fieldInfo = (fieldName) => {
    const [name, key] = fieldName.split('_')
    return {name, key}
  }

  roomTitle = (title, roomNumber) => {
    return `${title} ${roomNumber}`
  }

  roomsCount = () => {
    const {rooms} = this.props
    this.roomsVisible = rooms.filter(r => r.visible === true).length
    return this.roomsVisible
  }

  onRemoveLastRoom = () => {
    const {
      removeRoomAction,
      filterApplyAction,
      rooms,
    } = this.props;
    const visibleRooms = rooms.filter(room => room.visible)
    const roomToRemove = visibleRooms[visibleRooms.length - 1]?.id

    removeRoomAction(roomToRemove)
    filterApplyAction(FILTER_ROOMS, true)
  }

  onRoomsCountChange = (value) => {
    const roomsCount = this.roomsCount()
    if (value > roomsCount) {
      this.onAddRoom()
    }
    if (value < roomsCount) {
      this.onRemoveLastRoom()
    }
  }

  isWarnDisplay = (values) => {
    const {
      titles: {
        hotelMaxAge,
        childAgeErrors,
      }
    } = this.props;

    if (!values || !childAgeErrors.childSelectErrors || !hotelMaxAge) {
      return false
    }

    return values.some(value => parseInt(value) > hotelMaxAge)
  }

  render() {
    const {
      titles: {
        adults,
        wantBookOneMoreRoom,
        showOneMoreRoom,
        room: roomTitle,
        remove: removeAnchor,
        childrenMaxAge,
        childrenMaxAmount,
        hotelMaxAge,
        from_to,
        children,
        child,
        childSelectDescription,
        childSelectSuffixSingle,
        childSelectSuffixPlural,
        childAgeErrors,
      },
      roomsCount,
      rooms,
    } = this.props
    const maxChildAge = parseInt(childrenMaxAge)
    const maxChildAmount = parseInt(childrenMaxAmount)
    const childrenAmountDesc = from_to?.replace('@to', childrenMaxAge)
    const adultsDefault = oneDayBooking ? ADULTS_ONE_DAY_BOOKING : defaultAdults
    const roomsToDisplay = rooms.filter(room => room.visible)
    const childSelectError = childAgeErrors.childSelectErrors.replace('@age', hotelMaxAge)

    return (
      <div className={cx('filter-rooms')}>
        <div className={cx('rooms-header', {'more-then-one': roomsCount > 1})}>
          <div className={cx('title')}>
            {roomTitle}
          </div>
          <PlusMinusField
            className={cx('rooms-header__counter')}
            onChange={this.onRoomsCountChange}
            minValue={1}
            maxValue={MAX_ROOMS}
            initialValue={1}
            value={this.roomsCount()}
          />
        </div>

        <div className={cx('room-fields-wrapper')}>
          {roomsToDisplay?.map((r, key) => (
            <div className={cx('room-field-el')} key={r.id}>
              <div className={cx('room-fields-field')}>
                <RoomHeader
                  title={this.roomTitle(roomTitle, key + 1, this.roomsCount())}
                  removeAnchor={removeAnchor}
                  roomsCount={this.roomsVisible}
                  roomId={r.id}
                  onRemoveClick={this.onRemoveRoom}
                />
                <RoomField
                  name={this.fieldName(FIELD_ADULTS, r.id)}
                  label={adults}
                  value={r.adults ? r.adults : adultsDefault}
                  minValue={ADULTS_MIN}
                  maxValue={ADULTS_MAX}
                  onChange={this.onGuestsChange}
                />
                <RoomField
                  name={this.fieldName(FIELD_CHILDREN, r.id)}
                  label={children}
                  description={childrenAmountDesc}
                  value={r.children ? r.children : CHILDREN_DEFAULT}
                  minValue={CHILDREN_MIN}
                  maxValue={maxChildAmount || CHILDREN_MAX}
                  onChange={(value, field) => this.onChildrenCountChange(value, field, r)}
                />
                {r?.childrenAge?.map((age, i) => (
                  <RoomSelectField
                    name={this.fieldName(FIELD_CHILDREN_AGE, `${r.id}:${i}`)}
                    key={`${r.id}:${i}`}
                    label={`${child} ${(i + 1)}`}
                    description={childSelectDescription}
                    wordings={{
                      singleLabel: childSelectSuffixSingle,
                      multipleLabel: childSelectSuffixPlural,
                    }}
                    value={age}
                    minValue={CHILDREN_AGE_MIN}
                    maxValue={maxChildAge || CHILDREN_AGE_MAX}
                    onChange={(value) => this.onChildrenAgeUpdate(r, i, value)}
                  />
                ))}
              </div>
              {this.isWarnDisplay(r?.childrenAge) && (
                <div className={cx('appartment-warning')}>{childSelectError}</div>
              )}
            </div>
          ))
          }
        </div>

        {isMultipleBookingEnabled && this.roomsVisible < MAX_ROOMS && (
          <div className={cx('footer')}>
            <Button
              theme="link"
              size="sm"
              className={cx('add-rooms-link')}
              onClick={this.onAddRoom}
            >
              {this.roomsVisible === 1 && wantBookOneMoreRoom}
              {this.roomsVisible > 1 && showOneMoreRoom}
            </Button>
          </div>
        )}
      </div>
    )
  }
}


Rooms.propTypes = {
  titles: PropTypes.shape({
    label: PropTypes.string,
    title: PropTypes.string,
    whoAccompaniesYou: PropTypes.string,
    adults: PropTypes.string,
    adultAge: PropTypes.string,
    child: PropTypes.string,
    children: PropTypes.string,
    childAge: PropTypes.string,
    baby: PropTypes.string,
    babies: PropTypes.string,
    babyAge: PropTypes.string,
    howManyRooms: PropTypes.string,
    room: PropTypes.string,
    rooms: PropTypes.string,
    apply: PropTypes.string,
    remove: PropTypes.string,
  }),
  rooms: PropTypes.arrayOf(PropTypes.object),
  guestsChangeAction: PropTypes.func.isRequired,
  filterApplyAction: PropTypes.func.isRequired,
  addRoomAction: PropTypes.func.isRequired,
  removeRoomAction: PropTypes.func.isRequired,
}

Rooms.defaultProps = {
  titles: null,
  rooms: null,
}

export default Rooms
